import { Q } from '@nozbe/watermelondb'
import dayjs from 'dayjs'
import LocalStorage from 'services/LocalStorage'
import {
  DocType,
  EventDoc,
  EventPriority,
  EventStatus,
  Exclusion,
  TaskType,
  UserSettingsDoc
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import Database from '../Database'
import { generateDefaultReminder } from '../utils/Event'
import { getArchivedGroups, isGroupMuted } from 'utils/api/userSettings'
import { isConvertedGroupId } from 'utils/groups'

import Logger from 'services/Logger'

import {
  isEventRecurrenceInTimePeriod,
  isEventWithinTimePeriod
} from 'components/calendar/CalendarView'
import { getDefaultTimezone } from 'index'
import { is24meApp } from 'utils/appType'
import { allGroups } from './GroupQueries'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { isEventBooked } from 'utils/event'
import { isUserAdminInGroup } from 'utils/api/groups'
import { ThirdPartyAccountByEmail } from './ThirdPartyCalendarsQuieries'
import { getCachedConvertedCalendarsFromLocalStorage } from 'utils/third_party_calendars'

export async function addTempEventToDate(
  startTime: Number,
  endTime: Number,
  groupId?: string,
  allDay?: boolean,
  docType?: DocType,
  labelId?: string,
  thirdPartyId?: string | undefined,
  rev?: string,
  taskType: TaskType = TaskType.meet
): Promise<Model<EventDoc>> {
  Logger.pink('TempEvent:DocType:', docType)
  Logger.pink('TempEvent:GroupID:', groupId)
  Logger.pink(
    'TempEvent:Start:',
    dayjs(Number(startTime) * 1000)
      .tz(allDay ? 'UTC' : getDefaultTimezone())
      .format('YYYY-MM-DD HH:mm:ss')
  )
  Logger.pink(
    'TempEvent:End:',
    dayjs(Number(endTime) * 1000)
      .tz(allDay ? 'UTC' : getDefaultTimezone())
      .format('YYYY-MM-DD HH:mm:ss')
  )

  /**
   * because of mismatch between formats of all day events, we need to *remove* extra day for our data source, in order to show
   * events correctly on a screen. The reason for that is that for example event which goes from October 9 to Octover 10
   * will be saved in our system as "from 2024-10-09 to 2024-10-10", when FullCalendar and some other providers support
   * format as such: "from 2024-10-09 to 2024-10-11", meaning, last day of the long event is day after */

  if (allDay && startTime !== endTime) {
    endTime = Number(endTime) - 86400
  }

  let fallbackType = DocType.EVENT

  if (is24meApp()) {
    fallbackType = DocType.TASK
  }

  const convertedCalendar = CurrentConvertedCalendars.find(
    (cal) => cal.localId === groupId
  )
  Logger.blue('TEMP EVENT convertedCalendar:', convertedCalendar)
  return await Database.write(async () => {
    const now = Date.now() / 1000
    const currentConvertedCalendar =
      getCachedConvertedCalendarsFromLocalStorage().find(
        (cal) => cal.localId === groupId && cal.primary
      )
    Logger.pink('TempEvent:Using converted calendar:', currentConvertedCalendar)
    Logger.pink(
      'TempEvent:Cached calendars:',
      getCachedConvertedCalendarsFromLocalStorage()
    )
    return Database.getCollection<EventDoc>(CollectionType.EVENT).create(
      (localEvent) => {
        localEvent.ObjectType = '1'
        localEvent._rev = rev
        localEvent.TaskType = taskType
        localEvent.Status = EventStatus.ACTIVE
        localEvent.Type = docType ?? fallbackType

        localEvent.GroupID =
          docType === DocType.TASK || docType === DocType.NOTE
            ? undefined
            : groupId
        localEvent.OwnerID = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        if (convertedCalendar) {
          localEvent.Color =
            convertedCalendar.backgroundColor ?? convertedCalendar.color
        }
        localEvent.PersonalOwnerId =
          taskType === TaskType.personalEvent
            ? currentConvertedCalendar?.primaryCalendarEmail
            : (LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
        localEvent.Rank = String(now)
        if (currentConvertedCalendar) {
          localEvent.Color =
            currentConvertedCalendar?.backgroundColor ??
            currentConvertedCalendar?.color
        }
        localEvent.OpenDate = String(now)
        localEvent.isDeleted = '0'
        localEvent.Text = ''
        localEvent.Priority = EventPriority.LOW
        localEvent.Label = labelId ? [{ LabelText: labelId }] : undefined
        localEvent.AllDay = allDay ? '1' : '0'
        localEvent.RequestConfirmation = '0'
        localEvent.StartDate = startTime.toString()
        localEvent.EndDate = endTime.toString()
        localEvent.ThirdPartyID = thirdPartyId
        localEvent.TimeZoneNameID = allDay ? 'UTC' : getDefaultTimezone()
        localEvent.Reminder = [
          generateDefaultReminder(
            localEvent.AllDay === '1',
            localEvent.StartDate
          )
        ]
      }
    )
  })
}

export const RemoveTempEvents = (fromWhere: string) => {
  Logger.pink('Removing temp events from:', fromWhere)
  LocalStorage.remove(LocalStorageKey.PENDING_EVENT_DATA)
  LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '0')
  LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)

  return Database.write(async () => {
    const eventsToRemove = await Database.getCollection<EventDoc>(
      CollectionType.EVENT
    )
      .query(Q.where('_id', ''))
      .fetch()
    eventsToRemove.map(async (event) => {
      await event.destroyPermanently()
    })
  })
}

export const RemoveEventsForGroup = (groupId: string) => {
  const query: Q.Clause[] = [Q.where('GroupID', Q.like(groupId))]

  return Database.write(async () => {
    const eventsToRemove = await Database.getCollection<EventDoc>(
      CollectionType.EVENT
    )
      .query(...query)
      .fetch()
    eventsToRemove.map(async (event) => {
      await event.destroyPermanently()
    })
  })
}

export const RemoveThirdPartyEvents = (groupId: string) => {
  const query: Q.Clause[] = [
    Q.where('GroupID', Q.like(groupId)),
    Q.where('_rev', Q.like(''))
  ]

  return Database.write(async () => {
    const eventsToRemove = await Database.getCollection<EventDoc>(
      CollectionType.EVENT
    )
      .query(...query)
      .fetch()
    eventsToRemove.map(async (event) => {
      await event.destroyPermanently()
    })
  })
}

export function excludeInstance(
  instanceTime: number,
  event: Model<EventDoc>,
  status: EventStatus
) {
  const timezone =
    (event.TimeZoneNameID?.length ?? 0) > 0
      ? event.TimeZoneNameID
      : getDefaultTimezone()

  const dayjsTime = dayjs(instanceTime)
    .add(
      dayjs(instanceTime) // Use the instance time, not current time
        .tz(timezone)
        .utcOffset(),
      'minute'
    )
    .tz('UTC', true)
    .startOf('day')
    .valueOf()
  const exDate = String(dayjsTime / 1000)

  // check if exclusion already exists, then just resolve promise
  const existingExclusion = event.Recurrence?.Exclusion?.find(
    (exclusion) => exclusion.Date === exDate
  )
  if (existingExclusion) {
    return Promise.resolve(event)
  }

  return Database.write(async () => {
    return event.update((local) => {
      let exclusions: Exclusion[] = local.Recurrence?.Exclusion ?? []

      const isConvertedGroup = isConvertedGroupId(event.GroupID ?? '')

      if (exclusions.length === 0 || typeof exclusions === 'string') {
        exclusions = []
      }

      exclusions.push({
        Date: exDate, //`${is24meApp() ? instanceTime / 1000 : exDate}`,
        Status: status,
        ExclusionDate: dayjs(instanceTime)
          .tz(getDefaultTimezone())
          .format('YYYYMMDDTHHmmss')
      })

      local.needSync = isConvertedGroup ? '1' : '0'
      local.Recurrence = {
        ...local.Recurrence,
        Exclusion: (exclusions ?? [])
          .reverse()
          .filter(
            (item: Exclusion, index: number, self: Exclusion[]) =>
              index === self.findIndex((i) => i.Date === item.Date)
          )
      }
    })
  })
}

export function stopRecurrenceAt(
  instanceTime: number,
  event?: Model<EventDoc>
) {
  return Database.write(async () => {
    return event?.update((local) => {
      const dayjsTime = dayjs(instanceTime).add(-1, 'day').valueOf()
      const endDate = String(dayjsTime / 1000)

      local.Recurrence = { ...local.Recurrence, EndDate: endDate }
    })
  })
}

/**
 *
 * @param startTime start time in millis
 * @param endTime in millis
 * @param event to copy from
 * @returns promise with event model created
 */
export async function createEventFromAnotherEvent(
  startTime: number,
  endTime: number,
  event: Model<EventDoc> | EventDoc
): Promise<EventDoc> {
  if (isConvertedGroupId(event.GroupID)) {
    const now = Date.now() / 1000
    let copy: EventDoc = {
      ...event,
      Rank: now.toString(),
      LastUpdate: now.toString(),
      StartDate: String(startTime / 1000),
      EndDate: String(endTime / 1000),
      ObjectType: '1',
      TaskType: TaskType.meet,
      Status: EventStatus.ACTIVE,
      ThirdPartyID: event.ThirdPartyID,
      Type: event.Type,
      GroupID: event.GroupID,
      OwnerID: event.OwnerID,
      PersonalOwnerId: event.PersonalOwnerId,
      OpenDate: String(now),
      isDeleted: '0',
      Text: event.Text,
      Notes: event.Notes,
      AllDay: event.AllDay,
      Location: event.Location,
      Priority: event.Priority
    }
    return Promise.resolve(copy)
  }

  return await Database.write(async () => {
    return Database.getCollection<EventDoc>(CollectionType.EVENT).create(
      (localEvent) => {
        const now = Date.now() / 1000

        localEvent._rev = 'copy'
        localEvent.Rank = now.toString()
        localEvent.LastUpdate = now.toString()
        localEvent.StartDate = String(startTime / 1000)
        localEvent.EndDate = String(endTime / 1000)
        localEvent.ObjectType = '1'
        localEvent.TaskType = TaskType.meet
        localEvent.Status = EventStatus.ACTIVE
        localEvent.ThirdPartyID = event.ThirdPartyID
        localEvent.Type = event.Type
        localEvent.GroupID = event.GroupID
        localEvent.OwnerID = event.OwnerID
        localEvent.Rank = String(now)
        localEvent.OpenDate = String(now)
        localEvent.isDeleted = '0'
        localEvent.Text = event.Text
        localEvent.Notes = event.Notes
        localEvent.AllDay = event.AllDay
        localEvent.Location = event.Location
        localEvent.Priority = event.Priority

        Logger.blue('Created copy', localEvent)
      }
    )
  })
}

export async function localEventByConvertedLocalId(id: string) {
  return (
    await Database.getCollection<EventDoc>(CollectionType.EVENT)
      .query(Q.where('local_id', id))
      .fetch()
  )[0]
}

export async function localEventById(id: string) {
  return (
    await Database.getCollection<EventDoc>(CollectionType.EVENT)
      .query(Q.where('_id', id))
      .fetch()
  )[0]
}

export async function localEventByLocalId(id: string) {
  return (
    await Database.getCollection<EventDoc>(CollectionType.EVENT)
      .query(Q.where('id', id))
      .fetch()
  )[0]
}

export async function allPersonalCalendarItems() {
  return await Database.getCollection<EventDoc>(CollectionType.EVENT)
    .query(Q.where('Type', 'REGULAR_EVENT'))
    .fetch()
}

export async function allCalendarItems() {
  return await Database.getCollection<EventDoc>(CollectionType.EVENT)
    .query()
    .fetch()
}

export function allCalendarItemsForSync() {
  return Database.getCollection<EventDoc>(CollectionType.EVENT)
    .query(Q.where('needSync', '1'))
    .fetch()
}

export async function otherCalendarEvents(
  calendarId: string,
  fromISO: string,
  toISO: string,
  returnAll = false
) {
  Logger.blue(
    `OtherCalendarsEvents:${calendarId}:`,
    calendarId,
    'returnAll:',
    returnAll
  )
  return await Database.getCollection<EventDoc>(CollectionType.EVENT)
    .query(Q.where('GroupID', Q.like(calendarId)))
    .fetch()
    .then((events) => {
      return events.filter((event) => {
        if (returnAll) {
          return true
        }

        if (event.StartDate && event.EndDate) {
          return (
            (isEventWithinTimePeriod(event, dayjs(fromISO), dayjs(toISO)) ||
              (event.Recurrence &&
                isEventRecurrenceInTimePeriod(
                  event,
                  dayjs(fromISO),
                  dayjs(toISO)
                ))) &&
            event.GroupID === calendarId
          )
        }
        return false
      })
    })
}

export async function eventsForReminders(userSettings: Model<UserSettingsDoc>) {
  const archivedGroups = userSettings ? getArchivedGroups(userSettings) : []
  const groups = await allGroups()
  const query: Q.Clause[] = [Q.where('GroupID', Q.notIn(archivedGroups))]

  let eventsQuery = [...query, Q.where('Status', Q.notIn(['3', '4']))]
  const events = await Database.getCollection<EventDoc>(CollectionType.EVENT)
    .query(...eventsQuery)
    .fetch()
  return events
    .filter((event) => {
      if (
        event.Recurrence &&
        event.Recurrence.Unit &&
        event.Reminder?.length != 0
      ) {
        if (
          event.Recurrence.EndDate &&
          dayjs
            .unix(Number(event.Recurrence.EndDate))
            .isBefore(dayjs().startOf('day'))
        ) {
          return false
        }

        return true
      }

      return (
        event.Reminder?.length != 0 &&
        dayjs
          .unix(Number(event.StartDate))
          .isBefore(dayjs().startOf('day').add(33, 'day')) &&
        dayjs.unix(Number(event.StartDate)).isAfter(dayjs().startOf('day')) &&
        !isGroupMuted(userSettings, event.GroupID ?? '')
      )
    })
    .filter((event) => {
      if (
        isEventBooked(event) &&
        !isUserAdminInGroup(
          groups.find((group) => group._id === event.GroupID),
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        )
      )
        return false // we do not show reminders for booked events

      return true
    })
}
